/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import API, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import CompaniesHouseLookup from "@/components/app/CompaniesHouseLookup";

// Pre-fill defaults (so empty rows show "—" rather than undefined).
// `entity_type` gates whether the company fields are shown — when set
// to "company", we render Company Name + Registration Number; when set
// to "individual" (default) those rows are hidden in both edit and
// read-only views.
const FIELDS = [
  { key: "entity_type",    label: "Entity type",           type: "select",
    options: ["", "individual", "company"],
    optionLabels: { "": "—", "individual": "Individual", "company": "Company" } },
  { key: "company_name",            label: "Company name",                 type: "text",
    showIf: (form) => (form.entity_type || "individual") === "company" },
  { key: "company_registration_number", label: "Company registration no.", type: "text",
    showIf: (form) => (form.entity_type || "individual") === "company" },
  { key: "name",            label: "Name",                  type: "hidden" },
  { key: "first_name",      label: "First name",            type: "text" },
  { key: "middle_name",     label: "Middle name(s)",        type: "text" },
  { key: "surname",         label: "Surname",               type: "text" },
  { key: "phone",           label: "Phone",                 type: "tel"  },
  { key: "address",         label: "Residential address (for letters)",   type: "hidden" },
  // Structured address fields — individual inputs. The single-string
  // `address` above is kept (hidden) and recomposed from these on save
  // so existing letter/print merges keep working.
  { key: "house_number",    label: "Number (house / flat)", type: "text" },
  { key: "address_line1",   label: "Address line 1 (e.g. High Street)", type: "text" },
  { key: "address_line2",   label: "Address line 2 (optional)", type: "text" },
  { key: "town",            label: "Town / city",           type: "text" },
  { key: "county",          label: "County (optional)",     type: "text" },
  { key: "postcode",        label: "Postcode",              type: "text" },
  { key: "address_duration_months", label: "Time at current address", type: "select",
    // Buckets align with SmartSearch guidance — anything under 12mo
    // requires a previous address so the CRA search still matches.
    options: ["", "3", "9", "24", "60"],
    optionLabels: { "": "—", "3": "Less than 6 months", "9": "6 – 12 months",
                    "24": "1 – 3 years",              "60": "3+ years" } },
  { key: "previous_address", label: "Previous address",     type: "area",
    showIf: (form) => {
      const d = parseInt(form.address_duration_months, 10);
      return Number.isFinite(d) && d < 12;
    } },
  { key: "previous_address_duration_months", label: "Time at previous address", type: "select",
    options: ["", "12", "36", "60", "120"],
    optionLabels: { "": "—", "12": "Up to 1 year", "36": "1 – 3 years",
                    "60": "3 – 5 years",          "120": "5+ years" },
    showIf: (form) => {
      const d = parseInt(form.address_duration_months, 10);
      return Number.isFinite(d) && d < 12;
    } },
  { key: "dob",             label: "Date of birth",         type: "date" },
  { key: "ni_number",       label: "NI number",             type: "text" },
  { key: "marital_status",  label: "Marital status",        type: "select",
    options: ["", "Single", "Married", "Civil partnership", "Divorced", "Widowed"] },
  { key: "id_type",         label: "ID document type",      type: "select",
    options: ["", "Passport", "Driving licence", "National ID", "Other"] },
  { key: "id_number",       label: "ID document number",    type: "text" },
  { key: "id_expiry",       label: "ID expiry",             type: "date" },
  { key: "aml_status",      label: "AML status",            type: "select",
    options: ["", "Not started", "In progress", "Cleared", "Failed"] },
  { key: "source_of_funds", label: "Source of funds",       type: "area" },
  { key: "mortgage_status", label: "Mortgage status",       type: "select",
    options: ["", "No mortgage", "MIP obtained", "Mortgage offer received", "N/A"] },
  { key: "notes",           label: "Notes",                 type: "area" },
];

// Best-effort split of a legacy single "name" string into first / middle /
// surname so the individual inputs are pre-filled when the structured
// fields are empty (first token = first name, last token = surname, the
// remainder = middle name(s)).
function splitName(full) {
  const parts = (full || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: "", middle_name: "", surname: "" };
  if (parts.length === 1) return { first_name: parts[0], middle_name: "", surname: "" };
  if (parts.length === 2) return { first_name: parts[0], middle_name: "", surname: parts[1] };
  return { first_name: parts[0], middle_name: parts.slice(1, -1).join(" "), surname: parts[parts.length - 1] };
}

const AML_PILL_COLORS = {
  "Cleared": "bg-emerald-50 text-emerald-700",
  "Failed": "bg-rose-50 text-rose-700",
  "In progress": "bg-amber-50 text-amber-700",
  "Not started": "bg-slate-100 text-slate-600",
};

/**
 * Modal for viewing & editing a single case client's extended profile.
 * Solicitors only. Email is shown read-only — to change email, remove and
 * re-add the client (because the user record is keyed by email).
 *
 * Props:
 *   open           — controlled boolean
 *   onOpenChange   — close callback
 *   caseId         — id of the case
 *   client         — full client object from caseData.clients[*]
 *   onSaved(case)  — callback with the updated case after a successful PUT
 *   readOnly       — true for non-solicitor viewers (renders fields as text)
 */
export default function ClientDetailsDialog({ open, onOpenChange, caseId, client, onSaved, readOnly = false }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!client) return;
    const init = {};
    // Numeric-typed fields come off Mongo as integers; the <select>
    // renders string options so we stringify here to keep the current
    // option highlighted when the dialog opens.
    const INT_KEYS = ["address_duration_months", "previous_address_duration_months"];
    FIELDS.forEach((f) => {
      const raw = client[f.key];
      if (INT_KEYS.includes(f.key) && (raw === 0 || raw)) {
        init[f.key] = String(raw);
      } else {
        init[f.key] = raw || "";
      }
    });
    // Pre-fill the individual name inputs from the legacy single "name"
    // when the structured parts are empty, so the solicitor sees proper
    // First / Middle / Surname instead of a blank form.
    if (!init.first_name && !init.surname && client.name) {
      const s = splitName(client.name);
      init.first_name = s.first_name;
      init.middle_name = init.middle_name || s.middle_name;
      init.surname = s.surname;
    }
    // Pre-fill address line 1 from the legacy freeform address when no
    // structured address exists, so nothing is lost and it's editable.
    if (!init.address_line1 && !init.house_number && client.address) {
      init.address_line1 = client.address;
    }
    setForm(init);
  }, [client]);

  if (!client) return null;
  const rowKey = client.id || `pending:${client.email}`;

  const handleSave = async () => {
    setSaving(true);
    try {
      // Integer-typed fields must be coerced — the <select> emits strings
      // and empty string fails Pydantic's Optional[int] validator.
      const INT_KEYS = ["address_duration_months", "previous_address_duration_months"];
      const payload = {};
      // When the entity is set to individual, force-clear any leftover
      // company values so they don't render on letters/contracts. The
      // FIELDS loop below would skip those rows because they're hidden,
      // so we clear them explicitly here.
      const isIndividual = (form.entity_type || "individual") !== "company";
      FIELDS.forEach((f) => {
        // Only send keys that actually changed (avoid clobbering with same value)
        if ((client[f.key] || "") !== (form[f.key] || "")) {
          const raw = form[f.key];
          if (INT_KEYS.includes(f.key)) {
            const n = parseInt(raw, 10);
            payload[f.key] = Number.isFinite(n) ? n : null;
          } else {
            payload[f.key] = raw || "";
          }
        }
      });
      if (isIndividual) {
        if (client.company_name && payload.company_name === undefined) payload.company_name = "";
        if (client.company_registration_number && payload.company_registration_number === undefined) payload.company_registration_number = "";
      }
      // Recompose the single-string `name` and `address` from the
      // individual inputs so existing letter/print merges, SDLT, HMLR and
      // the Didit reference stay in sync with what the solicitor typed.
      const composedName = [form.first_name, form.middle_name, form.surname]
        .map((x) => (x || "").trim()).filter(Boolean).join(" ");
      if (composedName && composedName !== (client.name || "")) payload.name = composedName;
      const composedAddr = [form.house_number, form.address_line1, form.address_line2, form.town, form.county, form.postcode]
        .map((x) => (x || "").trim()).filter(Boolean).join(", ");
      if (composedAddr && composedAddr !== (client.address || "")) payload.address = composedAddr;
      if (!Object.keys(payload).length) {
        onOpenChange(false);
        return;
      }
      const { data } = await API.put(`/cases/${caseId}/clients/${encodeURIComponent(rowKey)}/details`, payload);
      onSaved?.(data);
      toast.success("Client details saved");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="client-details-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
            {readOnly ? "Client details" : "Edit client details"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Email (read-only — keyed to the user account) */}
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Email</Label>
            <div className="text-sm text-slate-900 font-medium" data-testid="cd-email">
              {client.email}
              {client.pending_account && (
                <span className="ml-2 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                  Pending account
                </span>
              )}
            </div>
          </div>

          {FIELDS.map((f) => {
            if (f.type === "hidden") return null;
            if (f.showIf && !f.showIf(form)) return null;
            const val = form[f.key] ?? "";
            // When we reach the company_name row in edit mode, render the
            // Companies House lookup widget directly above it so picking a
            // result auto-fills both name + reg number in one go.
            const renderCHLookup = !readOnly && f.key === "company_name" && (form.entity_type === "company");
            if (readOnly) {
              const display = (f.optionLabels && f.optionLabels[val]) || val || "—";
              return (
                <div key={f.key} className="space-y-1.5">
                  <Label className="text-xs text-slate-500">{f.label}</Label>
                  {f.key === "aml_status" && val ? (
                    <span
                      className={`inline-flex text-xs font-medium px-2 py-0.5 rounded-full ${AML_PILL_COLORS[val] || "bg-slate-100 text-slate-600"}`}
                      data-testid={`cd-${f.key}`}
                    >
                      {val}
                    </span>
                  ) : (
                    <div className="text-sm text-slate-900 whitespace-pre-wrap" data-testid={`cd-${f.key}`}>{display}</div>
                  )}
                </div>
              );
            }
            return (
              <div key={f.key} className="space-y-1.5">
                {renderCHLookup && (
                  <div className="rounded-md border border-violet-200 bg-violet-50/40 p-3 space-y-2 mb-2">
                    <Label className="text-xs text-violet-800 font-semibold">Companies House lookup</Label>
                    <CompaniesHouseLookup
                      companyNumber={form.company_registration_number}
                      onSelect={(profile) => {
                        const regAddr = profile.registered_office_address || "";
                        setForm((p) => ({
                          ...p,
                          company_name: profile.company_name || p.company_name,
                          company_registration_number: profile.company_number || p.company_registration_number,
                          address_line1: p.address_line1 || regAddr || p.address_line1,
                          address: p.address || regAddr || "",
                        }));
                      }}
                    />
                  </div>
                )}
                <Label className="text-xs text-slate-700">{f.label}</Label>
                {f.type === "area" ? (
                  <Textarea
                    data-testid={`cd-${f.key}-input`}
                    value={val}
                    onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                    rows={3}
                    className="text-sm"
                  />
                ) : f.type === "select" ? (
                  <Select
                    value={val || "_none_"}
                    onValueChange={(v) => setForm((p) => ({ ...p, [f.key]: v === "_none_" ? "" : v }))}
                  >
                    <SelectTrigger data-testid={`cd-${f.key}-select`} className="text-sm">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {f.options.map((opt) => (
                        <SelectItem key={opt || "_none_"} value={opt || "_none_"}>
                          {(f.optionLabels && f.optionLabels[opt]) || opt || "—"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={f.type}
                    data-testid={`cd-${f.key}-input`}
                    value={val}
                    onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                    className="text-sm"
                  />
                )}
              </div>
            );
          })}
        </div>

        {!readOnly && (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cd-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="cd-save-btn"
              style={{ background: "#100f5d", color: "#fff" }}
            >
              {saving ? "Saving…" : "Save details"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
